import type { RouteRecordRaw } from "vue-router";

function joinRoutePath(parentPath: string, path: string): string {
  if (path.startsWith("/")) return path;
  const parent = parentPath === "/" ? "" : parentPath.replace(/\/$/, "");
  const child = path.replace(/^\//, "");
  return `/${[parent, child].filter(Boolean).join("/")}`.replace(/\/+/g, "/");
}

function normalizeParameterNames(path: string): string {
  return path.replace(/:[$A-Z_a-z][$\w]*/g, ":param");
}

interface RegisteredPath {
  record: RouteRecordRaw;
  isAlias: boolean;
  isDefaultChild: boolean;
  parent?: RouteRecordRaw;
}

interface PendingRedirect {
  record: RouteRecordRaw;
  path: string;
  parentPath: string;
}

export function validateRouteRecords(records: readonly RouteRecordRaw[]): void {
  const names = new Map<string, string>();
  const paths = new Map<string, RegisteredPath>();
  const redirects: PendingRedirect[] = [];
  const errors: string[] = [];
  const enforcesShellContract = records.some(
    (record) => record.meta?.shell !== undefined,
  );

  function registerPath(
    record: RouteRecordRaw,
    rawPath: string,
    options: Omit<RegisteredPath, "record">,
  ): void {
    const path = normalizeParameterNames(rawPath);
    const previous = paths.get(path);
    const isParentDefaultPair =
      previous !== undefined &&
      ((options.isDefaultChild && options.parent === previous.record) ||
        (previous.isDefaultChild && previous.parent === record));
    const isExplicitRedirect =
      previous !== undefined &&
      (previous.record.redirect !== undefined || record.redirect !== undefined);

    if (previous && !isParentDefaultPair && !isExplicitRedirect) {
      const kind = options.isAlias || previous.isAlias ? "alias" : "path";
      errors.push(`Ambiguous route ${kind} "${path}"`);
      return;
    }

    if (!previous || (previous.record.redirect && !record.redirect)) {
      paths.set(path, { record, ...options });
    }
  }

  function visit(
    routeRecords: readonly RouteRecordRaw[],
    parentPath = "",
    parent?: RouteRecordRaw,
    inheritedMeta: Record<string, unknown> = {},
  ): void {
    for (const record of routeRecords) {
      const path = joinRoutePath(parentPath, record.path);
      const meta = { ...inheritedMeta, ...record.meta };

      if (enforcesShellContract) {
        const policies = [
          "title",
          "breadcrumb",
          "shell",
          "navbar",
          "sidepanel",
          "contentWidth",
        ] as const;
        for (const policy of policies) {
          if (meta[policy] === undefined) {
            errors.push(`Route "${path}" has no reviewed ${policy} policy`);
          }
        }
        if (meta.shell === "none" && meta.navbar !== "hidden") {
          errors.push(`Route "${path}" with shell "none" must hide the navbar`);
        }
        if (meta.shell !== "application" && meta.sidepanel === "global") {
          errors.push(
            `Route "${path}" with shell "${meta.shell}" must hide the sidepanel`,
          );
        }
      }
      if (record.name !== undefined) {
        const name = String(record.name);
        const previousPath = names.get(name);
        if (previousPath !== undefined) {
          errors.push(
            `Duplicate route name "${name}" at ${previousPath} and ${path}`,
          );
        } else {
          names.set(name, path);
        }
      }

      registerPath(record, path, {
        isAlias: false,
        isDefaultChild: record.path === "" && parent !== undefined,
        parent,
      });

      const aliases = Array.isArray(record.alias)
        ? record.alias
        : record.alias
          ? [record.alias]
          : [];
      for (const alias of aliases) {
        registerPath(record, joinRoutePath(parentPath, alias), {
          isAlias: true,
          isDefaultChild: false,
          parent,
        });
      }

      if (record.redirect !== undefined) {
        redirects.push({ record, path, parentPath });
      }

      if (record.children) visit(record.children, path, record, meta);
    }
  }

  visit(records);

  for (const { record, path, parentPath } of redirects) {
    const redirect = record.redirect;
    if (typeof redirect === "function" || redirect === undefined) continue;

    if (typeof redirect === "string") {
      const target = normalizeParameterNames(
        joinRoutePath(parentPath, redirect.split(/[?#]/, 1)[0]),
      );
      if (!paths.has(target)) {
        errors.push(`Unknown redirect target path "${target}" at ${path}`);
      }
      continue;
    }

    if (
      "name" in redirect &&
      redirect.name !== undefined &&
      !names.has(String(redirect.name))
    ) {
      errors.push(
        `Unknown redirect target name "${String(redirect.name)}" at ${path}`,
      );
    } else if ("path" in redirect && redirect.path !== undefined) {
      const target = normalizeParameterNames(
        joinRoutePath(parentPath, redirect.path),
      );
      if (!paths.has(target)) {
        errors.push(`Unknown redirect target path "${target}" at ${path}`);
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(errors.join("\n"));
  }
}
